import { useState, useEffect } from "react";
import { API_ROOT } from "../utils/apiConfig";

/**
 * Custom hook to fetch all pricing data from the backend
 * @returns {Object} - { plans, faqs, settings, isLoading, error }
 */
export const useAllPricingData = () => {
  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [plansRes, faqsRes, settingsRes] = await Promise.all([
          fetch(`${API_ROOT}/pricing/plans`),
          fetch(`${API_ROOT}/pricing/faqs`),
          fetch(`${API_ROOT}/pricing/settings`),
        ]);

        if (!plansRes.ok) {
          throw new Error(`Failed to fetch plans: ${plansRes.statusText}`);
        }
        if (!faqsRes.ok) {
          throw new Error(`Failed to fetch FAQs: ${faqsRes.statusText}`);
        }
        if (!settingsRes.ok) {
          throw new Error(
            `Failed to fetch settings: ${settingsRes.statusText}`,
          );
        }

        const plansData = await plansRes.json();
        const faqsData = await faqsRes.json();
        const settingsData = await settingsRes.json();

        setPlans(plansData.data || []);
        setFaqs(faqsData.data || []);
        setSettings(settingsData.data || null);
      } catch (err) {
        console.error("Error fetching pricing data:", err);
        setError(err.message);
        setPlans([]);
        setFaqs([]);
        setSettings(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  return {
    plans,
    faqs,
    settings,
    isLoading,
    error,
  };
};
